/**
 * @module analytics
 * @description Typed Google Analytics helper with event queue.
 *
 * Buffers events in a Svelte writable store so calls made before
 * gtag.js finishes loading are replayed automatically once the
 * script fires its callback. Provides convenience wrappers for
 * common GA4 operations (user properties, user ID, config, events)
 * so components never have to touch `window.gtag` directly.
 *
 * @example
 * ```ts
 * import { ga } from '$lib/analytics';
 *
 * // Set user properties (queued if gtag not ready yet)
 * ga.setUserProperties({ plan: 'pro' });
 *
 * // Send a custom event
 * ga.event('feature_used', { feature_name: 'dark_mode' });
 *
 * // Set user ID for cross-device tracking
 * ga.setUserId('user-123');
 * ```
 */
import { writable } from "svelte/store";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GAEvent {
  type: "event";
  name: string;
  params?: Record<string, unknown>;
}

interface GASet {
  type: "set";
  field: string;
  params: Record<string, unknown> | string;
}

interface GAConfig {
  type: "config";
  id: string;
  params?: Record<string, unknown>;
}

type GACmd = GAEvent | GASet | GAConfig;

// ---------------------------------------------------------------------------
// Queue (store-based, same pattern as @beyonk/svelte-google-analytics)
// ---------------------------------------------------------------------------

const queue = writable<GACmd[]>([]);

/** Flush queued commands into the real dataLayer. */
function flush(): void {
  queue.update((cmds) => {
    for (const cmd of cmds) {
      switch (cmd.type) {
        case "event":
          window.gtag?.("event", cmd.name, cmd.params);
          break;
        case "set":
          window.gtag?.("set", cmd.field, cmd.params);
          break;
        case "config":
          window.gtag?.("config", cmd.id, cmd.params);
          break;
      }
    }
    return [];
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Whether gtag is available in the global scope. */
function isReady(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Send an arbitrary GA4 event. Works even before gtag.js loads
 * (the event is queued and replayed once the script fires its callback).
 */
function event(name: string, params?: Record<string, unknown>): void {
  if (isReady()) {
    window.gtag?.("event", name, params);
  } else {
    queue.update((q) => [...q, { type: "event", name, params }]);
  }
}

/**
 * Call `gtag('set', ...)` to update user properties, user ID, or other
 * config fields after initial page load.
 */
function set(field: string, params: Record<string, unknown> | string): void {
  if (isReady()) {
    window.gtag?.("set", field, params);
  } else {
    queue.update((q) => [...q, { type: "set", field, params }]);
  }
}

/**
 * Convenience wrapper for `gtag('set', 'user_properties', ...)`.
 */
function setUserProperties(
  props: Record<string, string | number | boolean>,
): void {
  set("user_properties", props);
}

/**
 * Convenience wrapper for `gtag('set', 'userId', ...)`.
 */
function setUserId(id: string): void {
  set("userId", id);
}

/**
 * Send a `gtag('config', ...)` call. Useful for adding a second
 * GA4 measurement ID at runtime or updating config options.
 */
function config(id: string, params?: Record<string, unknown>): void {
  if (isReady()) {
    window.gtag?.("config", id, params);
  } else {
    queue.update((q) => [...q, { type: "config", id, params }]);
  }
}

// ---------------------------------------------------------------------------
// Useful event helpers (subset of GA4 recommended events)
// ---------------------------------------------------------------------------

function selectContent(contentType: string, itemId: string): void {
  event("select_content", { content_type: contentType, item_id: itemId });
}

function search(searchTerm: string): void {
  event("search", { search_term: searchTerm });
}

function share(contentType: string, itemId: string, method?: string): void {
  event("share", { content_type: contentType, item_id: itemId, method });
}

function signUp(method: string): void {
  event("sign_up", { method });
}

function login(method: string): void {
  event("login", { method });
}

function generateLead(value?: number, currency?: string): void {
  event("generate_lead", { value, currency });
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const ga = {
  /** Flush the internal queue (called by GoogleAnalytics.svelte on mount). */
  flush,
  /** Whether gtag.js is loaded and ready. */
  isReady,
  // Core
  event,
  set,
  config,
  setUserProperties,
  setUserId,
  // Event helpers
  selectContent,
  search,
  share,
  signUp,
  login,
  generateLead,
} as const;
